import type {
  ExtensionInstallationSummary,
  ExtensionInvocationLog,
} from "@api-client";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Card, CardContent } from "@sylviq/ui-react-web/components/ui/card";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { useRef, useState } from "react";
import { ExtensionDocumentation } from "./ExtensionDocumentation";
import type { ExtensionUpdate } from "./extension-updates";
import {
  type ExtensionInstallationLifecycleAction,
  type ExtensionInstallationLogFilters,
  disableExtensionInstallation,
  enableExtensionInstallation,
  listExtensionInstallationLogs,
  uninstallExtensionInstallation,
} from "@/lib/api";

export type InstallationListState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: ExtensionInstallationSummary[] };

type InvocationLogListState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "loaded";
      items: ExtensionInvocationLog[];
      nextCursor: string | null;
      loadedPages: number;
      loadingOlder: boolean;
      olderError?: string;
    };

type InvocationLogFilters = {
  eventType: string;
  fromUtcDate: string;
  throughUtcDate: string;
  trigger: string;
  status: ExtensionInvocationLog["status"] | "";
};

type LifecycleNotice = { tone: "status" | "alert"; message: string };

const EMPTY_INVOCATION_LOG_FILTERS: InvocationLogFilters = {
  eventType: "",
  fromUtcDate: "",
  throughUtcDate: "",
  trigger: "",
  status: "",
};
const INVOCATION_LOG_STATUSES: ExtensionInvocationLog["status"][] = [
  "pending",
  "running",
  "succeeded",
  "failed",
];

function installationDetailKey(
  organizationId: string,
  installationId: string,
): string {
  return JSON.stringify([organizationId, installationId]);
}

function hasInvocationLogFilters(filters: InvocationLogFilters): boolean {
  return Boolean(
    filters.eventType ||
      filters.fromUtcDate ||
      filters.throughUtcDate ||
      filters.trigger ||
      filters.status,
  );
}

function apiInvocationLogFilters(
  filters: InvocationLogFilters,
): ExtensionInstallationLogFilters | undefined {
  const query: ExtensionInstallationLogFilters = {};
  if (filters.eventType) query.event_type = filters.eventType;
  if (filters.fromUtcDate) query.from_date = filters.fromUtcDate;
  if (filters.throughUtcDate) query.through_date = filters.throughUtcDate;
  if (filters.trigger) query.trigger = filters.trigger;
  if (filters.status) query.status = filters.status;
  return Object.keys(query).length > 0 ? query : undefined;
}

function errorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("status" in error)) return null;
  return typeof error.status === "number" ? error.status : null;
}

function errorCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("code" in error)) return undefined;
  return typeof error.code === "string" ? error.code : undefined;
}

function installationStateTone(state: string): "success" | "warning" | "neutral" {
  if (state === "enabled") return "success";
  if (state === "disabled") return "warning";
  return "neutral";
}

function lifecycleActionLabel(action: ExtensionInstallationLifecycleAction): string {
  if (action === "enable") return "enabled";
  if (action === "disable") return "disabled";
  return "uninstalled";
}

export function ExtensionsInstalledPanel({
  organizationId,
  userId,
  canManage,
  organizationName,
  installations,
  updates,
  updatingInstallationId,
  onUpdate,
  onRefreshInstallations,
}: {
  organizationId: string;
  userId: string;
  canManage: boolean;
  organizationName: string;
  installations: InstallationListState;
  updates: ExtensionUpdate[];
  updatingInstallationId: string | null;
  onUpdate: (update: ExtensionUpdate) => void;
  onRefreshInstallations: () => Promise<InstallationListState>;
}) {
  const [invocationLogsByInstallation, setInvocationLogsByInstallation] = useState<
    Record<string, InvocationLogListState>
  >({});
  const [invocationLogFiltersByInstallation, setInvocationLogFiltersByInstallation] = useState<
    Record<string, InvocationLogFilters>
  >({});
  const [appliedInvocationLogFiltersByInstallation, setAppliedInvocationLogFiltersByInstallation] = useState<
    Record<string, InvocationLogFilters>
  >({});
  const [invocationLogEventTypesByInstallation, setInvocationLogEventTypesByInstallation] = useState<
    Record<string, string[]>
  >({});
  const [invocationLogHookPointsByInstallation, setInvocationLogHookPointsByInstallation] = useState<
    Record<string, string[]>
  >({});
  const [openInstallationDetail, setOpenInstallationDetail] = useState<string | null>(null);
  const [lifecycleBusy, setLifecycleBusy] = useState<{
    installationId: string;
    action: ExtensionInstallationLifecycleAction;
  } | null>(null);
  const [lifecycleNotice, setLifecycleNotice] = useState<LifecycleNotice | null>(null);
  const [uninstallIntent, setUninstallIntent] = useState<ExtensionInstallationSummary | null>(null);
  const [detailsIntent, setDetailsIntent] = useState<{ id: string; context: string } | null>(null);
  const detailContext = JSON.stringify([organizationId, organizationId, userId]);
  const detailsInstallation = detailsIntent?.context === detailContext && installations.status === "loaded"
    ? installations.items.find((item) => item.id === detailsIntent.id) : undefined;
  const installationDetailRequestIds = useRef(new Map<string, number>());

  const refreshInvocationLogs = async (
    installation: ExtensionInstallationSummary,
    key: string,
    filters: InvocationLogFilters,
  ) => {
    const requestId = (installationDetailRequestIds.current.get(key) ?? 0) + 1;
    installationDetailRequestIds.current.set(key, requestId);
    setInvocationLogsByInstallation((current) => ({
      ...current,
      [key]: { status: "loading" },
    }));

    try {
      const query = apiInvocationLogFilters(filters);
      const page = query
        ? await listExtensionInstallationLogs(
            organizationId,
            installation.id,
            query,
          )
        : await listExtensionInstallationLogs(
            organizationId,
            installation.id,
          );
      if (installationDetailRequestIds.current.get(key) !== requestId) return;
      setInvocationLogsByInstallation((current) => ({
        ...current,
        [key]: {
          status: "loaded",
          items: page.events,
          nextCursor: page.next_cursor,
          loadedPages: 1,
          loadingOlder: false,
        },
      }));
      setAppliedInvocationLogFiltersByInstallation((current) => ({
        ...current,
        [key]: filters,
      }));
      if (!query) {
        setInvocationLogEventTypesByInstallation((current) => ({
          ...current,
          [key]: [...new Set(page.events.map((item) => item.event_type))].sort(),
        }));
        setInvocationLogHookPointsByInstallation((current) => ({
          ...current,
          [key]: [...new Set(page.events.map((item) => item.trigger))].sort(),
        }));
      }
    } catch {
      if (installationDetailRequestIds.current.get(key) !== requestId) return;
      setInvocationLogsByInstallation((current) => ({
        ...current,
        [key]: {
          status: "error",
          message: "Invocation logs could not be loaded. Please try again.",
        },
      }));
    }
  };

  const loadOlderInvocationLogs = async (
    installation: ExtensionInstallationSummary,
    key: string,
    filters: InvocationLogFilters,
  ) => {
    const current = invocationLogsByInstallation[key];
    if (current?.status !== "loaded" || !current.nextCursor || current.loadingOlder) return;

    const cursor = current.nextCursor;
    const requestId = (installationDetailRequestIds.current.get(key) ?? 0) + 1;
    installationDetailRequestIds.current.set(key, requestId);
    setInvocationLogsByInstallation((states) => {
      const state = states[key];
      return state?.status === "loaded"
        ? { ...states, [key]: { ...state, loadingOlder: true, olderError: undefined } }
        : states;
    });

    try {
      const page = await listExtensionInstallationLogs(
        organizationId,
        installation.id,
        apiInvocationLogFilters(filters),
        cursor,
      );
      if (installationDetailRequestIds.current.get(key) !== requestId) return;
      setInvocationLogsByInstallation((states) => {
        const state = states[key];
        return state?.status === "loaded"
          ? {
              ...states,
              [key]: {
                ...state,
                items: [...state.items, ...page.events],
                nextCursor: page.next_cursor,
                loadedPages: state.loadedPages + 1,
                loadingOlder: false,
                olderError: undefined,
              },
            }
          : states;
      });
    } catch {
      if (installationDetailRequestIds.current.get(key) !== requestId) return;
      setInvocationLogsByInstallation((states) => {
        const state = states[key];
        return state?.status === "loaded"
          ? {
              ...states,
              [key]: {
                ...state,
                loadingOlder: false,
                olderError: "Older invocation logs could not be loaded. Please try again.",
              },
            }
          : states;
      });
    }
  };

  const toggleInvocationLogs = async (installation: ExtensionInstallationSummary) => {
    const key = installationDetailKey(organizationId, installation.id);
    const logState = invocationLogsByInstallation[key];
    if (openInstallationDetail === key && logState?.status !== "error") {
      setOpenInstallationDetail(null);
      return;
    }
    setOpenInstallationDetail(key);
    if (logState?.status === "loaded" || logState?.status === "loading") return;
    await refreshInvocationLogs(installation, key, EMPTY_INVOCATION_LOG_FILTERS);
  };

  const runInstallationLifecycle = async (
    action: ExtensionInstallationLifecycleAction,
    installation: ExtensionInstallationSummary,
  ) => {
    if (lifecycleBusy !== null) return;
    const displayName = `${installation.publisher}/${installation.key}`;
    setLifecycleBusy({ installationId: installation.id, action });
    setLifecycleNotice(null);
    try {
      const result = action === "enable"
        ? await enableExtensionInstallation(organizationId, installation.id)
        : action === "disable"
          ? await disableExtensionInstallation(organizationId, installation.id)
          : await uninstallExtensionInstallation(organizationId, installation.id);
      setUninstallIntent(null);
      const expectedState = lifecycleActionLabel(action);
      if (result.id !== installation.id || result.state !== expectedState) {
        const unconfirmed = await onRefreshInstallations();
        setLifecycleNotice({
          tone: unconfirmed.status === "error" ? "alert" : "status",
          message: unconfirmed.status === "error"
            ? "The server response could not be confirmed and the installation list could not be refreshed. Refresh to verify the current state."
            : "The server response could not be confirmed. The installation list was refreshed; verify the current state.",
        });
        return;
      }
      const refreshed = await onRefreshInstallations();
      setLifecycleNotice({
        tone: refreshed.status === "error" ? "alert" : "status",
        message: refreshed.status === "error"
          ? `The server recorded ${displayName} as ${expectedState}, but the installation list could not be refreshed. Refresh to confirm.`
          : `${displayName} ${expectedState}.`,
      });
    } catch (error) {
      setUninstallIntent(null);
      const status = errorStatus(error);
      const code = errorCode(error);
      if (status === 409 && code === "already_uninstalled") {
        const refreshed = await onRefreshInstallations();
        setLifecycleNotice({
          tone: "status",
          message: refreshed.status === "error"
            ? "This installation is already uninstalled, but the list could not be refreshed. Refresh to confirm."
            : "This installation is already uninstalled. The list was refreshed.",
        });
      } else if (status === 409 && code === "enabled_dependents_exist") {
        setLifecycleNotice({
          tone: "alert",
          message: "Another enabled extension in this organization depends on this extension. Disable or uninstall the dependent extension first.",
        });
      } else if (status === 409 && code === "invalid_state") {
        const refreshed = await onRefreshInstallations();
        setLifecycleNotice({
          tone: "status",
          message: refreshed.status === "error"
            ? "This installation's state changed, and the latest list could not be loaded. Refresh to check the current state."
            : "This installation's state changed. The latest list was loaded; review the current state before retrying.",
        });
      } else if (status === 403) {
        setLifecycleNotice({
          tone: "alert",
          message: "Only the organization owner can enable, disable, or uninstall extensions.",
        });
      } else if (status === 401) {
        setLifecycleNotice({
          tone: "alert",
          message: "This change could not be confirmed (401). Sign out and sign in to Console again before retrying.",
        });
      } else {
        setLifecycleNotice({
          tone: "alert",
          message: `Could not ${action} ${displayName}. Refresh the list and try again.`,
        });
      }
    } finally {
      setLifecycleBusy(null);
    }
  };

  return (
    <section aria-labelledby="installations-heading" className="space-y-4">
      <h2 id="installations-heading" className="text-lg font-semibold">
        Installed extensions
      </h2>
      {lifecycleNotice ? (
        <p
          className={`text-sm ${lifecycleNotice.tone === "alert" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]"}`}
          role={lifecycleNotice.tone}
        >
          {lifecycleNotice.message}
        </p>
      ) : null}
      {installations.status === "loading" ? (
        <p role="status" className="text-sm text-[var(--muted-foreground)]">
          Loading installed extensions...
        </p>
      ) : null}
      {installations.status === "error" ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {installations.message}
        </p>
      ) : null}
      {installations.status === "loaded" && installations.items.length === 0 ? (
        <EmptyState
          title="No extensions installed"
          reason={`There are no installed extensions in ${organizationName}.`}
        />
      ) : null}
      {installations.status === "loaded" && installations.items.length > 0 ? (
        <ul className="space-y-3">
          {installations.items.map((installation) => {
            const displayName = `${installation.publisher}/${installation.key}`;
            const logKey = installationDetailKey(
              organizationId,
              installation.id,
            );
            const logState = invocationLogsByInstallation[logKey];
            const logFilters =
              invocationLogFiltersByInstallation[logKey] ?? EMPTY_INVOCATION_LOG_FILTERS;
            const appliedLogFilters =
              appliedInvocationLogFiltersByInstallation[logKey] ?? EMPTY_INVOCATION_LOG_FILTERS;
            const visibleLogs = logState?.status === "loaded"
              ? logState.items
              : [];
            const logEventTypes = invocationLogEventTypesByInstallation[logKey] ?? [];
            const logHookPoints = invocationLogHookPointsByInstallation[logKey] ?? [];
            const hasLogFilters =
              hasInvocationLogFilters(logFilters) || hasInvocationLogFilters(appliedLogFilters);
            const busyAction = lifecycleBusy?.installationId === installation.id
              ? lifecycleBusy.action
              : null;
            const update = updates.find(
              (candidate) => candidate.installation.id === installation.id,
            );
            const controlsDisabled =
              lifecycleBusy !== null || updatingInstallationId !== null;
            return (
              <li key={installation.id}>
                <Card>
                  <CardContent className="space-y-4 pt-4">
                    <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_auto_auto] lg:items-center">
                      <div className="min-w-0">
                        <h3 className="font-semibold"><Button variant="ghost" size="sm" className="h-auto max-w-full break-all text-left" aria-label={`Details for installed ${displayName}`} onClick={() => setDetailsIntent({ id: installation.id, context: detailContext })}>{displayName}</Button></h3>
                        <p className="text-sm text-[var(--muted-foreground)]">
                          Publisher {installation.publisher} · Key {installation.key} · Version {installation.version}
                          {update ? ` · Update ${update.latest.version} available` : ""}
                        </p>
                      </div>
                      <Badge tone={installationStateTone(installation.state)}>
                        {installation.state}
                      </Badge>
                      <div className="flex flex-wrap gap-2 lg:justify-end">
                        {canManage && update ? (
                          <Button
                            aria-label={`Update ${displayName} to version ${update.latest.version}`}
                            disabled={controlsDisabled}
                            onClick={() => onUpdate(update)}
                            size="sm"
                          >
                            {updatingInstallationId === installation.id
                              ? "Updating..."
                              : `Update to ${update.latest.version}`}
                          </Button>
                        ) : null}
                        {canManage && installation.state === "disabled" ? (
                          <Button
                            aria-label={`Enable ${displayName}`}
                            disabled={controlsDisabled}
                            onClick={() => void runInstallationLifecycle("enable", installation)}
                            size="sm"
                          >
                            {busyAction === "enable" ? "Enabling..." : "Enable"}
                          </Button>
                        ) : null}
                        {canManage && installation.state === "enabled" ? (
                          <Button
                            aria-label={`Disable ${displayName}`}
                            disabled={controlsDisabled}
                            onClick={() => void runInstallationLifecycle("disable", installation)}
                            size="sm"
                            variant="outline"
                          >
                            {busyAction === "disable" ? "Disabling..." : "Disable"}
                          </Button>
                        ) : null}
                        {canManage && installation.state !== "uninstalled" ? (
                          <Button
                            aria-label={`Uninstall ${displayName}`}
                            disabled={controlsDisabled}
                            onClick={() => setUninstallIntent(installation)}
                            size="sm"
                            variant="destructive"
                          >
                            Uninstall
                          </Button>
                        ) : null}
                        <Button
                          aria-label={`View invocation logs for ${displayName}`}
                          onClick={() => void toggleInvocationLogs(installation)}
                          size="sm"
                          variant="outline"
                        >
                          {openInstallationDetail === logKey
                            ? logState?.status === "error"
                              ? "Retry logs"
                              : "Hide logs"
                            : "View logs"}
                        </Button>
                      </div>
                    </div>
                    {openInstallationDetail === logKey ? (
                      <div aria-label="Invocation logs" className="space-y-3 border-t pt-4">
                        <p className="text-sm text-[var(--muted-foreground)]">
                          These are metadata-only logs, newest first, in pages of up to 100 events for this installation. Filters apply within each selected page; date bounds are inclusive UTC calendar dates. Load older to continue through prior events. Extension-authored payloads are not stored for new events and are omitted from historical rows.
                        </p>
                        <p className="text-sm text-[var(--muted-foreground)]">
                          Trigger and status belong to the invocation. Status is its current state; an event does not carry a separate historical status.
                        </p>
                        {logState?.status === "loading" ? (
                          <p role="status" className="text-sm">Loading invocation logs...</p>
                        ) : null}
                        {logState?.status === "error" ? (
                          <p role="alert" className="text-sm text-[var(--danger)]">{logState.message}</p>
                        ) : null}
                        {logState?.status === "loaded" ? (
                          <div className="space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
                              <Select
                                aria-label="Filter logs by event type"
                                onChange={(eventType) =>
                                  setInvocationLogFiltersByInstallation((current) => ({
                                    ...current,
                                    [logKey]: { ...logFilters, eventType },
                                  }))
                                }
                                options={logEventTypes.map((eventType) => ({
                                  value: eventType,
                                  label: eventType,
                                }))}
                                placeholder="All event types"
                                value={logFilters.eventType}
                              />
                              <Input
                                aria-label="From date (UTC)"
                                onChange={(event) =>
                                  setInvocationLogFiltersByInstallation((current) => ({
                                    ...current,
                                    [logKey]: {
                                      ...logFilters,
                                      fromUtcDate: event.target.value,
                                    },
                                  }))
                                }
                                type="date"
                                value={logFilters.fromUtcDate}
                              />
                              <Input
                                aria-label="Through date (UTC)"
                                onChange={(event) =>
                                  setInvocationLogFiltersByInstallation((current) => ({
                                    ...current,
                                    [logKey]: {
                                      ...logFilters,
                                      throughUtcDate: event.target.value,
                                    },
                                  }))
                                }
                                type="date"
                                value={logFilters.throughUtcDate}
                              />
                              <Select
                                aria-label="Filter logs by trigger"
                                onChange={(trigger) =>
                                  setInvocationLogFiltersByInstallation((current) => ({
                                    ...current,
                                    [logKey]: { ...logFilters, trigger },
                                  }))
                                }
                                options={logHookPoints.map((trigger) => ({
                                  value: trigger,
                                  label: trigger,
                                }))}
                                placeholder="All triggers"
                                value={logFilters.trigger}
                              />
                              <Select
                                aria-label="Filter logs by current invocation status"
                                onChange={(status) =>
                                  setInvocationLogFiltersByInstallation((current) => ({
                                    ...current,
                                    [logKey]: {
                                      ...logFilters,
                                      status: status as InvocationLogFilters["status"],
                                    },
                                  }))
                                }
                                options={INVOCATION_LOG_STATUSES.map((status) => ({
                                  value: status,
                                  label: status,
                                }))}
                                placeholder="All current statuses"
                                value={logFilters.status}
                              />
                              <Button
                                aria-label={`Apply log filters for ${displayName}`}
                                onClick={() =>
                                  void refreshInvocationLogs(
                                    installation,
                                    logKey,
                                    logFilters,
                                  )
                                }
                              >
                                Apply filters
                              </Button>
                              <Button
                                aria-label={`Clear log filters for ${displayName}`}
                                disabled={!hasLogFilters}
                                onClick={() => {
                                  setInvocationLogFiltersByInstallation((current) => ({
                                    ...current,
                                    [logKey]: EMPTY_INVOCATION_LOG_FILTERS,
                                  }));
                                  void refreshInvocationLogs(
                                    installation,
                                    logKey,
                                    EMPTY_INVOCATION_LOG_FILTERS,
                                  );
                                }}
                                variant="outline"
                              >
                                Clear filters
                              </Button>
                            </div>
                            {visibleLogs.length === 0 ? (
                              <EmptyState
                                title={hasInvocationLogFilters(appliedLogFilters)
                                  ? "No events match the selected filters"
                                  : "No invocation logs"}
                                reason={hasInvocationLogFilters(appliedLogFilters)
                                  ? logState.nextCursor
                                    ? "Adjust or clear the filters, or load older events to check the next page."
                                    : "No older page is available. Adjust or clear the applied event type, hook point, current status, or date filters."
                                  : "This installation has no events in the newest page returned by the API."}
                              />
                            ) : null}
                            {visibleLogs.map((log, index) => (
                              <article className="rounded-md border p-3" key={`${log.invocation_id}:${log.created_at}:${index}`}>
                                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                                  <div><dt className="text-[var(--muted-foreground)]">Invocation ID</dt><dd className="break-all">{log.invocation_id}</dd></div>
                                  <div><dt className="text-[var(--muted-foreground)]">Trigger</dt><dd className="break-all">{log.trigger}</dd></div>
                                  <div><dt className="text-[var(--muted-foreground)]">Current invocation status</dt><dd>{log.status}</dd></div>
                                  <div><dt className="text-[var(--muted-foreground)]">Event type</dt><dd>{log.event_type}</dd></div>
                                  <div><dt className="text-[var(--muted-foreground)]">Created at</dt><dd>{log.created_at}</dd></div>
                                </dl>
                                <p className="mt-3 text-sm text-[var(--muted-foreground)]">
                                  Payload content is omitted. This entry contains only server-derived invocation metadata.
                                </p>
                              </article>
                            ))}
                            {logState.olderError ? (
                              <p role="alert" className="text-sm text-[var(--danger)]">
                                {logState.olderError}
                              </p>
                            ) : null}
                            {logState.nextCursor ? (
                              <Button
                                aria-label={`Load older invocation logs for ${displayName}`}
                                disabled={logState.loadingOlder}
                                onClick={() =>
                                  void loadOlderInvocationLogs(
                                    installation,
                                    logKey,
                                    appliedLogFilters,
                                  )
                                }
                                variant="outline"
                              >
                                {logState.loadingOlder ? "Loading older logs..." : "Load older"}
                              </Button>
                            ) : logState.loadedPages > 1 ? (
                              <p className="text-sm text-[var(--muted-foreground)]">
                                No older invocation logs are available.
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      ) : null}
      <Dialog
        open={Boolean(detailsInstallation)} onOpenChange={(open) => { if (!open) setDetailsIntent(null); }}
        title="Installed extension"
        description="The installed release and its declared guides."
      >
        {detailsInstallation ? <div className="max-h-[min(60dvh,calc(100dvh-16rem))] space-y-4 overflow-y-auto">
          <p className="break-all font-medium">{detailsInstallation.publisher}/{detailsInstallation.key}</p>
          <dl className="space-y-2 text-sm">
            <div><dt className="font-medium">Installed version</dt><dd>{detailsInstallation.version} · {detailsInstallation.state}</dd></div>
            <div><dt className="font-medium">Enabled app scopes</dt><dd>{detailsInstallation.apps?.join(", ") || "No app scopes returned"}</dd></div>
          </dl>
          <ExtensionDocumentation publisher={detailsInstallation.publisher} extensionKey={detailsInstallation.key} metadata={detailsInstallation} />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => { setDetailsIntent(null); void toggleInvocationLogs(detailsInstallation); }}>Run history</Button>
          </div>
        </div> : null}
      </Dialog>
      <Dialog
        description={uninstallIntent
          ? `Records and history are preserved, but ${uninstallIntent.publisher}/${uninstallIntent.key} stops running in this organization. In-flight work is not cancelled.`
          : "Records and history are preserved, but the extension stops running in this organization. In-flight work is not cancelled."}
        onOpenChange={(open) => {
          if (!open && lifecycleBusy === null) setUninstallIntent(null);
        }}
        open={uninstallIntent !== null}
        title={uninstallIntent
          ? `Uninstall ${uninstallIntent.publisher}/${uninstallIntent.key}?`
          : "Uninstall extension?"}
      >
        <div className="flex justify-end gap-2">
          <Button
            disabled={lifecycleBusy !== null}
            onClick={() => setUninstallIntent(null)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            aria-label={uninstallIntent
              ? `Confirm uninstall of ${uninstallIntent.publisher}/${uninstallIntent.key}`
              : "Confirm uninstall"}
            disabled={lifecycleBusy !== null}
            onClick={() => {
              if (uninstallIntent) void runInstallationLifecycle("uninstall", uninstallIntent);
            }}
            variant="destructive"
          >
            {lifecycleBusy?.action === "uninstall" ? "Uninstalling..." : "Uninstall extension"}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}
